import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from './global-components/navbar-v2';
import PageHeader from './global-components/page-header';
import LegalContent from './section-components/legal-content';
import Footer from './global-components/footer-v2';

const TermsAndConditions = () => {
	const content = {
		kicker: 'Converzex — Website Terms of Service',
		meta: 'Effective Date: August 14, 2026 | Last Updated: August 14, 2026',
		sections: [
			{
				id: 'introduction',
				title: '1. Introduction',
				paragraphs: [
					'These Terms apply to your use of Converzex, including our website, software platform, AI tools, voice services, dashboards, integrations, subscription packages, and related services.',
					'Converzex is operated by DotZero Tech LLC at 5900 Balcones Drive STE 100 Austin, TX 78731,USA. By creating an account, purchasing a package, or using the Services, you agree to these Terms.'
				]
			},
			{
				id: 'definitions',
				title: '2. Definitions',
				list: [
					'Account: Your account used to access the Services.',
					'Customer / You: The individual or organization using the Services.',
					'Customer Data: Information submitted to or processed through the Services by or for the Customer.',
					'Services: Converzex software, website, Chat Agent, voice, lead, integration, analytics, billing, and related features.',
					'Third-Party Service: A service, platform, API, provider, or integration not owned or controlled by Converzex.'
				]
			},
			{
				id: 'services',
				title: '3. The Converzex Services',
				paragraphs: [
					'Converzex provides tools for customer conversations, lead capture and qualification, voice interactions, appointment scheduling, conversation and call management, integrations, and customer-engagement analytics.',
					'Available features depend on the selected package, configuration, usage limits, integrations, and any additional agreement.'
				]
			},
			{
				id: 'account-security',
				title: '4. Account Security',
				list: [
					'Provide accurate and current information when creating an account.',
					'Keep your login credentials secure.',
					'You are responsible for activity through your account and for assigning appropriate user permissions.',
					'Tell us promptly if you suspect unauthorized access or a security issue.',
					'Do not share or transfer account access in a way that violates your package or security controls.'
				]
			},
			{
				id: 'customer-data',
				title: '5. Customer Data and Responsibilities',
				paragraphs: [
					<>You retain your rights in Customer Data. You give Converzex the permissions needed to host, process, transmit, display, analyze, secure, maintain, and support the Services, including the AI training and improvement uses described in the <Link to="/privacy-policy">Privacy Policy</Link>.</>,
					'You are responsible for having the legal right to collect and process Customer Data and to provide it to Converzex.',
					'You are responsible for required privacy notices and consent for Chat Agent conversations, voice calls, call recording, marketing, messaging, and other processing.'
				]
			},
			{
				id: 'ai-features',
				title: '6. AI Features',
				paragraphs: [
					'Converzex uses AI and automated technology. AI-generated responses, summaries, classifications, recommendations, and other results may contain errors.',
					'Customers are responsible for reviewing and configuring AI behavior appropriately and should not use Converzex as a substitute for professional advice or as the sole basis for decisions that legally require human review.',
					<>Converzex may use Customer Data and content from customer websites to train and improve its AI models as described in the <Link to="/privacy-policy">Privacy Policy</Link>.</>
				]
			},
			{
				id: 'voice-recording',
				title: '7. Voice, Recording, and Messaging',
				paragraphs: [
					'Voice calls made or received through Converzex are recorded by default. Customers are responsible for providing legally required notices and obtaining required consent in every jurisdiction where calls take place.',
					'Customers using voice, SMS, WhatsApp, social messaging, or similar features must follow applicable communication, marketing, recording, consent, opt-out, do-not-contact, and consumer-protection requirements.',
					'The Services must not be used for unlawful robocalling, spam, harassment, impersonation, deceptive communications, or unauthorized marketing.'
				]
			},
			{
				id: 'integrations',
				title: '8. Integrations and Third-Party Services',
				paragraphs: [
					'Converzex may connect with websites, Shopify, HubSpot, CRM systems, social platforms, messaging services, payment providers, telephony providers, and other third-party applications.',
					'Third-party services have their own terms and policies. Converzex is not responsible for third-party availability, functionality, security, or privacy practices.'
				]
			},
			{
				id: 'packages-fees',
				title: '9. Packages, Fees, Billing, and Taxes',
				paragraphs: [
					'Paid Services are provided according to the package selected at purchase. Charges may include subscriptions, usage, voice or messaging charges, credits, setup fees, or other charges shown at purchase or in an applicable order.',
					'You authorize us or our payment provider to charge applicable fees according to your selected billing arrangement. Taxes or similar charges may apply unless stated otherwise.'
				]
			},
			{
				id: 'trial-renewal',
				title: '10. Free Trial, Renewal, and Cancellation',
				paragraphs: [
					'Converzex offers a 7-day free trial to new customers. If you do not cancel before the trial ends, your account will convert to a paid subscription and the selected package will be charged.',
					'Unless stated otherwise, subscriptions renew automatically for the same billing period. You may cancel through available account controls or by contacting us. Cancellation normally stops future renewals but does not automatically refund unused time in the current paid period.'
				]
			},
			{
				id: 'acceptable-use',
				title: '11. Acceptable Use',
				paragraphs: ['You may not use Converzex to:'],
				list: [
					'Break applicable laws or regulations.',
					'Commit fraud, impersonation, harassment, abuse, or deception.',
					'Send unlawful or unauthorized marketing communications.',
					'Collect personal information through deceptive or unlawful methods.',
					'Process highly sensitive information where the Services are not designed or configured for it.',
					'Transmit malware or harmful code.',
					'Attack, probe, bypass, or interfere with platform security.',
					'Reverse engineer or attempt to obtain source code except where law permits.',
					'Infringe intellectual-property or privacy rights.',
					'Facilitate unlawful activity.'
				]
			},
			{
				id: 'intellectual-property',
				title: '12. Intellectual Property',
				paragraphs: [
					'Converzex software, interfaces, documentation, branding, designs, technology, workflows, and underlying systems belong to DotZero Tech LLC or its licensors.',
					<>You receive a limited right to use the Services during your subscription. You retain ownership of Customer Data and content you provide, subject to the permissions needed to operate the Services and the AI uses described in the <Link to="/privacy-policy">Privacy Policy</Link>.</>
				]
			},
			{
				id: 'availability',
				title: '13. Service Availability and Changes',
				paragraphs: [
					'We aim to keep the Services reliable, but we do not guarantee uninterrupted or error-free availability. Services may be affected by maintenance, technical issues, internet connectivity, third-party outages, or events outside our reasonable control.',
					'We may add, change, or remove features as the Services evolve, while taking reasonable steps not to materially reduce contracted functionality without appropriate notice where required.'
				]
			},
			{
				id: 'no-guaranteed-results',
				title: '14. No Guaranteed Results',
				paragraphs: [
					'Converzex is designed to improve customer engagement, automation, lead handling, and operational efficiency. We do not guarantee any specific number of leads, appointments, sales, conversion rate, revenue, cost savings, or return on investment.'
				]
			},
			{
				id: 'suspension',
				title: '15. Suspension and Termination',
				paragraphs: [
					'We may suspend or terminate access when reasonably necessary due to non-payment, security risks, unlawful activity, serious breaches of these Terms, abuse of the Services, or legal or third-party requirements.'
				]
			},
			{
				id: 'disclaimers',
				title: '16. Disclaimers',
				paragraphs: [
					'To the extent permitted by law, the Services are provided on an “as is” and “as available” basis. We do not guarantee that AI output will always be accurate, complete, current, suitable, or uninterrupted.'
				]
			},
			{
				id: 'liability',
				title: '17. Limitation of Liability',
				paragraphs: [
					'To the extent permitted by law, Converzex and DotZero Tech LLC will not be responsible for indirect, incidental, special, consequential, exemplary, or punitive damages, or for loss of profits, revenue, goodwill, business opportunities, or data arising from use of the Services.',
					'To the extent permitted by law, our total liability related to the Services will not exceed the fees you paid to Converzex during the 12 months before the event giving rise to the claim.'
				]
			},
			{
				id: 'indemnification',
				title: '18. Indemnification',
				paragraphs: [
					'To the extent permitted by law, you agree to protect and hold harmless Converzex, DotZero Tech LLC, and their personnel from third-party claims resulting from your unlawful use of the Services, Customer Data, failure to obtain required consent, violation of these Terms, or failure to obtain required rights or permissions.'
				]
			},
			{
				id: 'governing-law',
				title: '19. Governing Law and Disputes',
				paragraphs: [
					'These Terms are governed by the laws of the State of Florida, United States. Subject to mandatory rights available under applicable law, disputes will be handled by the state or federal courts located in Miami-Dade County, Florida.'
				]
			},
			{
				id: 'changes',
				title: '20. Changes to These Terms',
				paragraphs: [
					'We may update these Terms as our Services or legal requirements change. Updated Terms will be posted on the website with a revised date.'
				]
			},
			{
				id: 'contact',
				title: '21. Contact',
				paragraphs: [
					<>Questions about these Terms may be sent to <a href="mailto:legal@converzex.com">legal@converzex.com</a> or DotZero Tech LLC, 5900 Balcones Drive STE 100 Austin, TX 78731,USA.</>
				]
			}
		]
	};

	return (
		<div>
			<Navbar />
			<PageHeader headertitle="Terms and Conditions" />
			<LegalContent {...content} />
			<Footer />
		</div>
	);
};

export default TermsAndConditions;
